import { Column, Row } from "@silevis/reactgrid";
import { useEffect, useState } from "react";
import { renameField } from "../../General/Helpers";
import { useData } from "../Contexts/DataProvider";
import BaseTable from "./BaseTable";
import {
  budgetPriorityColumns,
  columnWidth,
  DIGITS_PREFIX,
  DIGITS_WITH_UNDERSCORE,
  isDigitsOnly,
  renameHeaderRow,
} from "./TableHelpers";

const sortColumns = (columns: Column[]): Column[] => {
  const isPriority = (col: Column) =>
    budgetPriorityColumns.includes(String(col.columnId));
  return [
    ...columns.filter(isPriority),
    ...columns
      .filter((c) => !isPriority(c))
      .sort((a, b) => {
        const aId = renameField(String(a.columnId));
        const bId = renameField(String(b.columnId));

        const aMatch = aId.match(DIGITS_WITH_UNDERSCORE);
        const bMatch = bId.match(DIGITS_WITH_UNDERSCORE);
        if (aMatch && bMatch) {
          const [, aPre, aSuf] = aMatch;
          const [, bPre, bSuf] = bMatch;
          if (aPre !== bPre) return aPre.localeCompare(bPre);
          if (aSuf && bSuf) return Number(aSuf) - Number(bSuf);
        }
        if (isDigitsOnly(aId) && isDigitsOnly(bId)) {
          return Number(aId) - Number(bId);
        }
        return aId.localeCompare(bId);
      }),
  ];
};

const buildColumns = (data: any[], visibleGroups?: string[]): Column[] => {
  const uniqueColumns = new Set<string>();
  data.forEach((item: any) => {
    Object.keys(item).forEach((key) => {
      if (
        key !== "@odata.etag" &&
        key !== "cr864_budgetrecordcopyid" &&
        key != "_ownerid_value"
      )
        uniqueColumns.add(key);
    });
  });
  let columnsArray: Column[] = [];
  budgetPriorityColumns.forEach((columnId) => {
    columnsArray.push({
      columnId,
      width: columnWidth(columnId),
      resizable: true,
    });
    uniqueColumns.delete(columnId);
  });

  uniqueColumns.forEach((key) => {
    const match = renameField(key).match(DIGITS_PREFIX);
    if (
      !visibleGroups ||
      visibleGroups.length === 0 ||
      (match && visibleGroups.includes(match[1]))
    ) {
      columnsArray.push({ columnId: key, width: 90, resizable: true });
    }
  });

  return sortColumns(columnsArray);
};

const buildRows = (
  data: any[],
  columns: Column[],
  submitted: boolean
): Row[] => {
  const rowHeight = 35;
  const headerRow: any = renameHeaderRow(columns);
  const dataRows = data.map((row, idx) => ({
    rowId: idx,
    height: rowHeight,
    cells: columns.map((col) => {
      const value = row[col.columnId];
      const readOnly = true;
      if (typeof value === "number") {
        return { type: "number", value, groupId: col.columnId, readOnly };
      } else if (typeof value === "string") {
        return { type: "text", text: value, groupId: col.columnId, readOnly };
      } else {
        return { type: "text", text: "", groupId: col.columnId, readOnly };
      }
    }),
    id: row.budgetrecordcopyid,
  }));
  return [headerRow, ...dataRows];
};

interface OriginalTableProps {
  visibleGroups?: string[];
}

const OriginalTable = ({ visibleGroups = [] }: OriginalTableProps) => {
  const { data } = useData();
  const [columns, setColumns] = useState<Column[]>([]);
  const [rows, setRows] = useState<Row[]>([]);

  useEffect(() => {
    if (data.value && data.value.length > 0) {
      const cols = buildColumns(data.value, visibleGroups);
      setColumns(cols);
      setRows(buildRows(data.value, cols, true));
    } else {
      setColumns([]);
      setRows([]);
    }
  }, [data, visibleGroups]);

  return (
    <BaseTable
      columns={columns}
      setColumns={setColumns}
      rows={rows}
      stickyLeftColumns={8}
      onCellsChanged={() => {}}
      modifyCellValues={() => {}}
    />
  );
};

export default OriginalTable;
